// ══════════════════════════════════════════════════════════════════════════════
// PAGE MES ABONNÉS - Style harmonisé avec Mon Espace (Liste de tâches)
// AbracadaWorld - Cockpit Particulier
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Search, MessageCircle, UserMinus, 
  ChevronRight, Clock, CheckCircle, X, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function MesAbonnes({ onAskAbra }) {
  const { user } = useCurrentUser();
  const [abonnes, setAbonnes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('Tous');
  const [filterCounts, setFilterCounts] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAbonne, setSelectedAbonne] = useState(null);
  const isDarkMode = false;
  
  // Refs et états pour le scroll des filtres
  const scrollContainerRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);

  const filters = ['Tous', 'Récents', 'Actifs', 'Vérifiés'];
  
  // Gérer le scroll des filtres
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const DEMO_ABONNES = [];

  useEffect(() => {
    setLoading(true);
    if (user?.isDemo) {
      setAbonnes(DEMO_ABONNES);
      setFilterCounts({ 'Tous': 0, 'Récents': 0, 'Actifs': 0, 'Vérifiés': 0 });
    } else {
      setAbonnes([]);
      setFilterCounts({ 'Tous': 0, 'Récents': 0, 'Actifs': 0, 'Vérifiés': 0 });
    }
    setLoading(false);
  }, [user]);

  const handleMessage = (abonne) => {
    toast.info(`Envoyer un message à ${abonne.name}`);
  };

  const handleRemove = (id) => {
    if (window.confirm('Retirer cet abonné ?')) {
      setAbonnes(prev => prev.filter(a => a.id !== id));
      toast.success('Abonné retiré');
    }
  };

  const filteredAbonnes = abonnes.filter(abonne => {
    const matchesSearch = abonne.name?.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    
    switch (activeFilter) {
      case 'Récents': return true; // Could filter by date
      case 'Actifs': return abonne.isActive;
      case 'Vérifiés': return abonne.verified;
      default: return true;
    }
  });

  const totalAbonnes = filterCounts['Tous'] || abonnes.length || 0;

  return (
    <div className="h-full flex flex-col">
      {/* Header - Style identique à Mes Notes */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <div>
            <h1 className={`text-[16px] sm:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#1e3a5f]'}`}>
              Mes abonnés
            </h1>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Les personnes qui te suivent sur la plateforme
            </p>
          </div>
        </div>

        {/* Stats rapides */}
        <div className="flex items-center gap-4 mt-3">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
            isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
          }`}>
            <Users size={14} className="text-orange-500" />
            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {totalAbonnes} abonné{totalAbonnes > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Recherche */}
      <div className="mb-3">
        <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher un abonné..."
            className={`flex-1 bg-transparent border-0 outline-none text-xs placeholder-gray-400 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
          />
        </div>
      </div>

      {/* Filtres - Style identique à Mes Notes / Mes Messages */}
      <div className="flex-shrink-0 pb-3">
        <div className="relative flex items-center">
          <div 
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {filters.map((filter) => {
              const count = filterCounts[filter] || 0;
              
              return (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  data-testid={`filter-${filter.toLowerCase()}`}
                  className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                    activeFilter === filter
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  <span className="flex items-center gap-1">
                    {filter}
                    {count > 0 && (
                      <span className={`min-w-[18px] h-[18px] flex items-center justify-center text-[10px] rounded-full ${
                        activeFilter === filter 
                          ? 'bg-white/25 text-white' 
                          : 'bg-orange-500 text-white'
                      }`}>
                        {count}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
          
          {/* Flèche orange discrète */}
          {showScrollHint && (
            <div className="flex-shrink-0 flex items-center pointer-events-none">
              <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
            </div>
          )}
        </div>
      </div>

      {/* Liste des abonnés */}
      <div className={`flex-1 rounded-2xl overflow-hidden ${
        isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
      }`}>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredAbonnes.length === 0 ? (
          /* État vide - Style harmonisé */
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8 max-w-sm">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                isDarkMode ? 'bg-slate-700' : 'bg-gray-50'
              }`}>
                <Users size={28} className={isDarkMode ? 'text-gray-500' : 'text-gray-300'} />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {searchQuery ? 'Aucun résultat' : 'Aucun abonné'}
              </h3>
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {searchQuery ? 'Essaie avec un autre terme.' : 'Partage du contenu pour attirer des abonnés.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 overflow-y-auto h-full">
            {filteredAbonnes.map((abonne) => (
              <motion.div
                key={abonne.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`p-4 cursor-pointer transition-colors ${
                  isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'
                }`}
                data-testid={`abonne-item-${abonne.id}`}
                onClick={() => setSelectedAbonne(abonne)}
              >
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#2D5A8A] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                    {abonne.name?.charAt(0).toUpperCase()}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-semibold text-sm truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {abonne.name}
                      </h3>
                      {abonne.verified && (
                        <CheckCircle size={14} className="text-blue-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {abonne.bio || 'Aucune bio'}
                    </p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Abonné depuis {abonne.since}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleMessage(abonne); }}
                      className={`p-2 rounded-lg ${
                        isDarkMode ? 'hover:bg-slate-600' : 'hover:bg-gray-100'
                      }`}
                    >
                      <MessageCircle size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleRemove(abonne.id); }}
                      className={`p-2 rounded-lg ${
                        isDarkMode ? 'hover:bg-red-900/30' : 'hover:bg-red-50'
                      }`}
                    >
                      <UserMinus size={16} className="text-red-400" />
                    </button>
                    <ChevronRight size={16} className={isDarkMode ? 'text-gray-600' : 'text-gray-300'} />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
